/**
 * Reactive onboarding-completion hook (APP-002 §6).
 *
 * Wraps the storage-layer primitives in a small React state so the onboarding
 * gate, the route, and the "replay" affordance stay in sync. The hook never
 * holds sensitive data: the underlying marker is a boolean + version tag only.
 *
 * The gate re-reads the marker on every render (cheap) so that completing
 * onboarding in the OnboardingRoute and navigating to /dashboard is honored by
 * the gate without a separate state instance going stale (APP-002 §30:
 * prevent redirect loops). Mutations go through markCompleted/reset so
 * in-component state stays in sync.
 */
import { useCallback, useState } from 'react';
import {
  clearOnboardingCompletion,
  readOnboardingCompletion,
  writeOnboardingCompletion,
} from './onboardingCompletion';

export interface UseOnboardingCompletion {
  completed: boolean;
  /** Mark onboarding complete and persist the local marker. */
  markCompleted: () => void;
  /** Clear the marker so onboarding can be replayed. */
  reset: () => void;
}

export function useOnboardingCompletion(): UseOnboardingCompletion {
  // Component-local mirror so markCompleted/reset update the UI immediately.
  // Re-reading storage on every render keeps the AccountGate fresh across
  // navigation (e.g. onboarding → /dashboard) without a stale state instance.
  const [localCompleted, setLocalCompleted] = useState<boolean>(false);
  const storageCompleted = readOnboardingCompletion().completed;
  const completed = localCompleted || storageCompleted;

  const markCompleted = useCallback(() => {
    writeOnboardingCompletion(true);
    setLocalCompleted(true);
  }, []);

  const reset = useCallback(() => {
    clearOnboardingCompletion();
    setLocalCompleted(false);
  }, []);

  return { completed, markCompleted, reset };
}
